import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Loading Component - Design System Atmosférico v3.0
 * 
 * Animações:
 * - Shimmer: Para skeletons
 * - Generating Pulse: Para estados de IA processando
 * - Render Progress: Para exports/renders
 */

interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
    size?: "sm" | "md" | "lg";
}

/**
 * LoadingSpinner - Spinner com glow coral
 */
const LoadingSpinner = React.forwardRef<HTMLDivElement, LoadingSpinnerProps>(
    ({ className, size = "md", ...props }, ref) => {
        const sizeClasses = {
            sm: "h-4 w-4 border-2",
            md: "h-8 w-8 border-3",
            lg: "h-12 w-12 border-4",
        };

        return (
            <div
                ref={ref}
                className={cn(
                    "animate-spin rounded-full border-t-coral-electric border-r-coral-electric/30 border-b-coral-electric/10 border-l-coral-electric/5",
                    sizeClasses[size],
                    className
                )}
                {...props}
            />
        );
    }
);
LoadingSpinner.displayName = "LoadingSpinner";

/**
 * GeneratingIndicator - Para quando IA está processando
 * Com pulse coral e "Renderizando..." text
 */
interface GeneratingIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
    text?: string;
    frameCount?: { current: number; total: number };
}

const GeneratingIndicator = React.forwardRef<HTMLDivElement, GeneratingIndicatorProps>(
    ({ className, text = "Gerando...", frameCount, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "flex items-center gap-3 px-6 py-4 rounded-bold",
                "bg-void-soft border border-coral-electric/30",
                "animate-generating",
                className
            )}
            {...props}
        >
            <LoadingSpinner size="sm" />
            <div className="flex flex-col">
                <span className="text-caption text-coral-electric animate-aura-breathe">
                    {text}
                </span>
                {frameCount && (
                    <span className="text-micro text-textMuted">
                        Renderizando frame {frameCount.current}/{frameCount.total}
                    </span>
                )}
            </div>
        </div>
    )
);
GeneratingIndicator.displayName = "GeneratingIndicator";

/**
 * Skeleton - Loading placeholder com shimmer dourado
 */
interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: "text" | "card" | "avatar";
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
    ({ className, variant = "text", ...props }, ref) => {
        const variantClasses = {
            text: "h-4 w-full rounded-micro",
            card: "h-40 w-full rounded-bold",
            avatar: "h-12 w-12 rounded-full",
        };

        return (
            <div
                ref={ref}
                className={cn(
                    "animate-shimmer",
                    variantClasses[variant],
                    className
                )}
                {...props}
            />
        );
    }
);
Skeleton.displayName = "Skeleton";

/**
 * RenderProgress - Barra de progresso para exports
 * Com easing acelerado no final (psicologia de espera)
 */
interface RenderProgressProps extends React.HTMLAttributes<HTMLDivElement> {
    progress: number; // 0-100
    label?: string;
}

const RenderProgress = React.forwardRef<HTMLDivElement, RenderProgressProps>(
    ({ className, progress, label, ...props }, ref) => {
        // Psicologia: acelera visualmente no final
        const visualProgress = Math.min(100, progress + (progress > 80 ? (progress - 80) * 0.5 : 0));

        return (
            <div ref={ref} className={cn("w-full", className)} {...props}>
                {label && (
                    <div className="flex justify-between mb-2">
                        <span className="text-caption text-textSecondary">{label}</span>
                        <span className="text-caption text-gold-primary font-mono">{Math.round(progress)}%</span>
                    </div>
                )}
                <div className="h-2 w-full rounded-pill bg-void-deep overflow-hidden">
                    <div
                        className="h-full rounded-pill bg-gradient-to-r from-gold-primary to-gold-soft transition-all duration-flow ease-decelerate"
                        style={{ width: `${visualProgress}%` }}
                    />
                </div>
            </div>
        );
    }
);
RenderProgress.displayName = "RenderProgress";

export { LoadingSpinner, GeneratingIndicator, Skeleton, RenderProgress };
